import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Radio Button
 *
 * A branded radio button with gold focus ring, navy selection dot.
 * Respects prefers-reduced-motion via framer-motion.
 *
 * Usage:
 *   <RadioGroup value={selected} onChange={setSelected} label="Lunch Choice">
 *     <Radio value="chicken" label="Chicken Caesar" />
 *     <Radio value="pasta" label="Pasta Primavera" />
 *     <Radio value="salad" label="Garden Salad" hint="Vegetarian" />
 *   </RadioGroup>
 */

// ─── Context ────────────────────────────────────────────────────────────────

interface RadioGroupContextValue {
	name: string
	value?: string
	onChange?: (value: string) => void
	disabled?: boolean
	size: 'sm' | 'md' | 'lg'
}

const RadioGroupContext = React.createContext<RadioGroupContextValue>({
	name: '',
	size: 'md',
})

// ─── RadioGroup ─────────────────────────────────────────────────────────────

export interface RadioGroupProps {
	/** Group label */
	label?: string
	/** Group name (auto-generated if not provided) */
	name?: string
	/** Selected value */
	value?: string
	/** Change handler */
	onChange?: (value: string) => void
	/** Error message */
	error?: string
	/** Disabled state */
	disabled?: boolean
	/** Size */
	size?: 'sm' | 'md' | 'lg'
	/** Layout */
	horizontal?: boolean
	children: React.ReactNode
	className?: string
}

export function RadioGroup({
	label,
	name: providedName,
	value,
	onChange,
	error,
	disabled = false,
	size = 'md',
	horizontal = false,
	children,
	className,
}: RadioGroupProps) {
	const generatedName = React.useId()
	const name = providedName || generatedName

	return (
		<RadioGroupContext.Provider value={{ name, value, onChange, disabled, size }}>
			<fieldset className={cn('space-y-2', className)} role="radiogroup" aria-label={label}>
				{label && (
					<legend className="text-sm font-medium font-ui text-foreground mb-2">{label}</legend>
				)}
				<div className={cn(horizontal ? 'flex flex-wrap gap-4' : 'flex flex-col gap-2')}>
					{children}
				</div>
				{error && (
					<p className="text-xs text-error font-ui mt-1">{error}</p>
				)}
			</fieldset>
		</RadioGroupContext.Provider>
	)
}

// ─── Radio ──────────────────────────────────────────────────────────────────

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
	/** Radio value */
	value: string
	/** Label text */
	label?: string
	/** Helper text */
	hint?: string
}

const sizeStyles = {
	sm: { circle: 'w-4 h-4', dot: 'w-1.5 h-1.5', label: 'text-xs' },
	md: { circle: 'w-5 h-5', dot: 'w-2 h-2', label: 'text-sm' },
	lg: { circle: 'w-6 h-6', dot: 'w-2.5 h-2.5', label: 'text-base' },
}

export function Radio({
	value,
	label,
	hint,
	disabled: itemDisabled,
	className,
	id: providedId,
	children: _children,
	...props
}: RadioProps) {
	const { name, value: groupValue, onChange, disabled: groupDisabled, size } = React.useContext(RadioGroupContext)
	const generatedId = React.useId()
	const id = providedId || generatedId
	const s = sizeStyles[size]
	const isSelected = value === groupValue
	const isDisabled = itemDisabled ?? groupDisabled

	return (
		<div className={cn('flex items-start gap-2.5', className)}>
			<motion.button
				type="button"
				role="radio"
				id={id}
				aria-checked={isSelected}
				disabled={isDisabled}
				onClick={() => !isDisabled && onChange?.(value)}
				className={cn(
					'flex items-center justify-center flex-shrink-0 rounded-full border-2',
					'transition-colors duration-fast ease-default',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 focus-visible:ring-offset-2',
					'disabled:opacity-50 disabled:cursor-not-allowed',
					s.circle,
					isSelected
						? 'border-navy bg-navy'
						: 'border-input bg-background hover:border-navy-300',
				)}
				whileTap={isDisabled ? undefined : { scale: 0.9 }}
				transition={{ type: 'spring', stiffness: 400, damping: 25 }}
			>
				<AnimatePresence>
					{isSelected && (
						<motion.span
							className={cn('rounded-full bg-gold', s.dot)}
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							exit={{ scale: 0 }}
							transition={{ type: 'spring', stiffness: 500, damping: 30 }}
						/>
					)}
				</AnimatePresence>
			</motion.button>
			{(label || hint) && (
				<div className="flex flex-col min-w-0 pt-px">
					{label && (
						<label
							htmlFor={id}
							className={cn('font-ui font-medium text-foreground cursor-pointer', s.label, isDisabled && 'opacity-50 cursor-not-allowed')}
						>
							{label}
						</label>
					)}
					{hint && (
						<span className="text-xs text-muted-foreground font-ui">{hint}</span>
					)}
				</div>
			)}

			{/* Hidden native input for form submission */}
			<input
				type="radio"
				name={name}
				value={value}
				checked={isSelected}
				onChange={() => onChange?.(value)}
				disabled={isDisabled}
				className="sr-only"
				tabIndex={-1}
				aria-hidden="true"
				{...props}
			/>
		</div>
	)
}
